import { useState, useContext, useEffect, useCallback, useReducer, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Select from 'react-select';
import countries from 'i18n-iso-countries';
import enCountries from 'i18n-iso-countries/langs/en.json';
import fiCountries from 'i18n-iso-countries/langs/fi.json';
import svCountries from 'i18n-iso-countries/langs/sv.json';
import daCountries from 'i18n-iso-countries/langs/da.json';
import nbCountries from 'i18n-iso-countries/langs/nb.json';
import deCountries from 'i18n-iso-countries/langs/de.json';
import frCountries from 'i18n-iso-countries/langs/fr.json';
import plCountries from 'i18n-iso-countries/langs/pl.json';
import etCountries from 'i18n-iso-countries/langs/et.json';
import i18n, { getLang } from '../i18n';
import { getSelfRegistrationRoutes, getViewState } from '../utils/redirectUtils';
import { countryPhoneCodes, pinnedCountries } from '../utils/countryPhoneCodes';

const localeFiles = {
  en: enCountries,
  fi: fiCountries,
  sv: svCountries,
  da: daCountries,
  nb: nbCountries,
  no: nbCountries,
  de: deCountries,
  fr: frCountries,
  pl: plCountries,
  et: etCountries
};

import ThemeContext from '../contexts/ThemeContext';

import { signup, verify } from '../services/authService';
import { showError } from '../services/toastService';
import { logAnalytics } from '../services/analyticsLogger';
import {
  isTextFieldValid,
  validateFieldPhone,
  validateFieldEmail
} from '../utils/validateFields';
import { getUserFromLocalstorage, getData } from '../utils/localStorage';
import { RoundButton, CollapsedTextButton } from './Buttons';
import { StepInput } from './Input';
import Spinner from '../components/Spinner';
import Icon from '../components/Icon';
import NotUnicEmailInfoStep from '../components/NotUnicEmailInfoStep';
import InvalidDomainInfoStep from '../components/InvalidDomainInfoStep';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import {
  ContainerStepBox,
  ContentStepBox,
  StepsHeader,
  StepLable,
  FlexRow,
  FlexItem,
  StyledErrorMessage
} from './basicComponents';

import { customerServicePhone, customerServicePhoneLink } from '../utils/customerService';

const InfoBlock = styled.p`
  font-size: 12px;
  line-height: 150%;
  color: #151529;
  margin-top: 8px;
  a {
    cursor: pointer;
    text-decoration: underline;
  }
`;

const DebugBanner = styled.div`
  background-color: #fff3cd;
  border: 2px solid #ffc107;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 600;
  color: #856404;
  text-align: center;
`;

const DebugPayloadBox = styled.pre`
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 16px;
  margin: 16px 0;
  font-size: 12px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 400px;
  overflow-y: auto;
`;

const PhoneInputWrapper = styled.div`
  display: flex;
  align-items: stretch;
  min-height: 50px;
  border: 1px solid ${({ $focused, $error, $color }) => $error ? '#ff0000' : $focused ? $color : '#cccccc'};
  border-radius: 6px;
  transition: border-color 0.2s;
  background-color: #F5F8FB;
  &:hover {
    border-color: ${({ $error, $color }) => $error ? '#ff0000' : $color};
  }
`;

const CountryCodePrefix = styled.div`
  background-color: transparent;
  padding: 0 12px;
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #151529;
  border-right: 1px solid #e0e0e0;
  user-select: none;
`;

const PhoneInputStyled = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background-color: white;
  padding: 0 12px;
  font-size: 16px;
  color: #75706f;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
  &::placeholder {
    color: #929292;
  }
  &:focus {
    outline: none;
  }
`;

const StyledFlexItem = styled(FlexItem)`
  display: flex;
  margin-top: 16px;
  input {
    margin: 0;
    margin-right: 8px;
  }
  p {
    margin: 0;
    font-size: 12px;
  }
  @media (max-width: 768px) {
    margin-top: 8px;
  }
`;

const PhoneVerificationWrapper = styled.div`
  [class*="-control"] input:focus {
    box-shadow: none !important;
    outline: none !important;
  }
`;

const validatorMap = {
  fistName: isTextFieldValid,
  lastName: isTextFieldValid,
  email: validateFieldEmail,
  phone: validateFieldPhone
};

const delay = 1000;

export const RelationBlock = ({ type, isPersonal, setRelation }) => {
  return (
    <>
      <StepLable>{i18n(`is_personal_${type}`)}</StepLable>
      <StyledFlexItem>
        <input
          type='radio'
          data-test='contactInfoStepEmailIsPersonal'
          value='personal'
          checked={isPersonal}
          onChange={(e) => setRelation(e.target.checked, e.target.value)}
        />
        <p>{i18n(`${type}_for_personal`)}</p>
      </StyledFlexItem>
      <StyledFlexItem>
        <input
          type='radio'
          data-test='contactInfoStepEmailIsCorporate'
          value='corporate'
          checked={!isPersonal}
          onChange={(e) => setRelation(e.target.checked, e.target.value)}
        />
        <p>{i18n(`${type}_shared_with_colleagues`)}</p>
      </StyledFlexItem>
    </>
  );
};

export const EmailVerificationPage = () => {
  const { colors } = useContext(ThemeContext);
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  const [contactInfo, setContactInfo] = useState({
    fistName: '',
    lastName: '',
    email: '',
    isPersonalEmail: true
  });

  const [errors, setErrors] = useState({
    fistName: null,
    lastName: null,
    email: null
  });

  const [viewState, setViewState] = useState('contactInfo');

  useEffect(() => {
    if (isEmailVerified) {
      setTimeout(redirectToNextAuthStep, delay);
    }

    return () => clearTimeout(redirectToNextAuthStep, delay);
  }, [isEmailVerified, redirectToNextAuthStep]);

  const redirectToNextAuthStep = useCallback(() => {
    if (isEmailVerified) {
      history.replace(getSelfRegistrationRoutes().check_email);
    }
  }, [history, isEmailVerified]);

  const updateContactInfo = (name, value) => {
    if (typeof value === 'string') {
      setContactInfo(prev => {
        return { ...prev, [name]: value };
      });
    }
    if (name === 'isPersonalEmail') {
      setContactInfo(prev => {
        return { ...prev, isPersonalEmail: value };
      });
    }
  };

  const checkData = (key) => {
    if (key) {
      if (!isTextFieldValid(contactInfo.fistName)) {
        setErrors(prev => ({...prev, [key]: `${key}_requirement`}));
      } else {
        setErrors(prev => ({...prev, [key]: null}));
      }
    }
    return;
  };

  const isValidData = () => {
    return Object.keys(contactInfo).reduce((memo, key) => { // eslint-disable-line consistent-return
      if (memo && key === 'isPersonalEmail') return true;
      if (memo && validatorMap[key]) {
        return validatorMap[key](getFixedValue(contactInfo, key));
      }
    }, true);
  };

  const { executeRecaptcha } = useGoogleReCaptcha();

  const getFixedPhoneValue = (phoneValue) => {
    return phoneValue
      .replaceAll(' ', '')
      .replaceAll('_', '');
  };

  const getFixedValue = (data, field) =>
    field === 'phone' ? getFixedPhoneValue(data[field]) : data[field];

  const handleNext = async () => {
    setLoading(true);
    try {
      const body = {
        name: `${contactInfo.fistName} ${contactInfo.lastName}`,
        email: contactInfo.email,
        isPersonal: contactInfo.isPersonalEmail
      };
      const token = await executeRecaptcha('register');
      await signup(body);
      setIsEmailVerified(true);
      setLoading(false);
      logAnalytics('Self-registration signup success');
      localStorage.setItem('user', JSON.stringify({...body, recaptchaToken: token}));
    } catch (err) {
      console.log('ERROR:', err);
      logAnalytics('Self-registration signup error', {
        error: err.message
      });
      const stateName = getViewState(err.message);
      if (stateName) {
        logAnalytics(`Self-registration ${stateName} substep`, {
          name: `${contactInfo.fistName} ${contactInfo.lastName}`,
          email: contactInfo.email
        });
        setViewState(stateName);
      } else {
        if (err.message.includes('Contact info not unique')) {
          showError('Contact info not unique. Please try again.');
        } else {
          showError(i18n('error500'));
        }
      }
      setLoading(false);
    }
  };

  const setEmailRelation = (checked, value) => {
    if (checked) {
      if (value === 'personal') {
        updateContactInfo('isPersonalEmail', true);
      }
      if (value === 'corporate') {
        updateContactInfo('isPersonalEmail', false);
      }
    }
  };

  return (
    <>
      {(() => {
        switch (viewState) {
          case 'contactInfo':
            return (
              <ContainerStepBox>
                {loading && (<Spinner overlay={true} />)}
                <ContentStepBox>
                  <FlexRow>
                    <StepsHeader>{i18n('contact_information')}</StepsHeader>
                  </FlexRow>
                  <FlexRow>
                    <FlexItem>
                      <StepLable
                        data-test='contactInfoStepFirstNameLabel'
                      >
                        {i18n('first_name')}
                        <span> *</span>
                      </StepLable>
                      <StepInput
                        type="text"
                        value={contactInfo.fistName}
                        onChange={(value) => updateContactInfo('fistName', value)}
                        onBlur={() => checkData('fistName')}
                        data-test='contactInfoStepFirstNameInput'
                      />
                      {errors.fistName ? (
                        <StyledErrorMessage
                          color={colors.error}
                        >
                          {i18n(errors.fistName)}
                        </StyledErrorMessage>
                      ) : null}
                    </FlexItem>
                    <FlexItem>
                      <StepLable
                        data-test='contactInfoStepFirstLastLabel'
                      >
                        {i18n('last_name')}
                        <span> *</span>
                      </StepLable>
                      <StepInput
                        type="text"
                        value={contactInfo.lastName}
                        onChange={(value) => updateContactInfo('lastName', value)}
                        onBlur={() => checkData('lastName')}
                        data-test='contactInfoStepLastNameInput'
                      />
                      {errors.lastName ? (
                        <StyledErrorMessage
                          color={colors.error}
                        >
                          {i18n(errors.lastName)}
                        </StyledErrorMessage>
                      ) : null}
                    </FlexItem>
                  </FlexRow>
                  <FlexRow>
                    <FlexItem>
                      <StepLable
                        data-test='contactInfoStepEmailLabel'
                      >
                        {i18n('contact_info_email')}
                        <span> *</span>
                      </StepLable>
                      <StepInput
                        type="text"
                        placeholder={'xxxxx.xxxxxxxx@xxxxx.xx'}
                        value={contactInfo.email}
                        onChange={(value) => updateContactInfo('email', value)}
                        onBlur={() => checkData('email')}
                        data-test='contactInfoStepEmailInput'
                      />
                        {errors.email ? (
                        <StyledErrorMessage
                          color={colors.error}
                        >
                          {i18n(errors.email)}
                        </StyledErrorMessage>
                      ) : null}      
                    </FlexItem>
                  </FlexRow>
                  <FlexRow>
                    <FlexItem>
                      <RelationBlock 
                        type='email'
                        isPersonal={contactInfo.isPersonalEmail} 
                        setRelation={setEmailRelation}
                      />
                    </FlexItem>
                  </FlexRow>
                </ContentStepBox>
                <RoundButton
                  data-test='selfRegistrationNextBtn'
                  onClick={handleNext}
                  padding='25'
                  darkButton={true}
                  disabled={!isValidData()}
                >
                  <CollapsedTextButton>
                    <span>{i18n('continue')}</span>
                    <Icon
                      icon='chevron_right'
                      iconLib='material'
                      iconSize={30}
                      withoutContainer
                    />
                  </CollapsedTextButton>
                </RoundButton>
                <InfoBlock>
                  {i18n('already_have_an_account_part1')}
                  <a href='/login'>{i18n('already_have_an_account_part2')}</a>
                </InfoBlock>
              </ContainerStepBox>
            );
          case 'notUnicEmail':
            return <NotUnicEmailInfoStep />;
          case 'invalidDomain':
            return <InvalidDomainInfoStep />;
          default:
            return null;
        }
      })()}
    </>
  );
};

export const PhoneVerificationPage = () => {
  const { colors } = useContext(ThemeContext);
  const history = useHistory();
  const location = useLocation();
  const [user] = useState(getUserFromLocalstorage());

  const isDebugMode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('debug') === 'true';
  }, [location.search]);

  const [loading, setLoading] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    localNumber: '',
    countryCode: 'FI',
    isPersonalPhone: true
  });
  const [errors, setErrors] = useState({
    phone: null,
    notUniquePhone: false
  });
  const [debugPayload, setDebugPayload] = useState(null);
  const [phoneInputFocused, setPhoneInputFocused] = useState(false);

  const [isPhoneVerified, setIsPhoneVerified] = useState(false);

  const fullPhoneNumber = useMemo(() => {
    const phoneCode = countryPhoneCodes[contactInfo.countryCode] || '+358';
    return phoneCode + contactInfo.localNumber;
  }, [contactInfo.countryCode, contactInfo.localNumber]);

  const currentLang = getLang();

  const countryOptions = useMemo(() => {
    const isoLang = currentLang === 'no' ? 'nb' : currentLang;

    try {
      countries.registerLocale(localeFiles[isoLang] || localeFiles.en);
    } catch (error) {
      countries.registerLocale(localeFiles.en);
    }

    const countryNames = countries.getNames(isoLang, { select: 'official' });

    const pinnedOptions = pinnedCountries.map(code => ({
      value: code,
      label: `${countryNames[code]} ${countryPhoneCodes[code]}`,
      phoneCode: countryPhoneCodes[code],
      isPinned: true
    }));

    const allOptions = Object.keys(countryNames)
      .filter(code => countryPhoneCodes[code])
      .map(code => ({
        value: code,
        label: `${countryNames[code]} ${countryPhoneCodes[code]}`,
        phoneCode: countryPhoneCodes[code]
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return [
      ...pinnedOptions,
      { value: 'separator', label: '─────────────', isDisabled: true },
      ...allOptions
    ];
  }, [currentLang]);

  const countrySelectStyles = useMemo(() => ({
    control: (styles, { isFocused }) => ({
      ...styles,
      minHeight: '50px',
      boxShadow: 'none',
      borderColor: isFocused ? colors.tulkaMainColor : '#cccccc',
      ':hover': {
        borderColor: colors.tulkaMainColor
      }
    }),
    input: (styles) => ({
      ...styles,
      boxShadow: 'none !important',
      outline: 'none !important'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? colors.tulkaMainColor : 'white',
      ':hover': {
        backgroundColor: state.isDisabled ? 'white' : '#DFFFFE',
        color: state.isDisabled ? '#ccc' : colors.tulkaMainColor,
        cursor: state.isDisabled ? 'default' : 'pointer'
      }
    })
  }), [colors]);

  const redirectToNextAuthStep = useCallback(async () => {
    const userFromLocaleStorage = getUserFromLocalstorage();
    if (isPhoneVerified) {
      if (userFromLocaleStorage?.token) {
        history.replace(getSelfRegistrationRoutes().cost_center);
      } else {
        history.replace(getSelfRegistrationRoutes().check_phone);
      }
    }
  }, [history, isPhoneVerified]);

  useEffect(() => {
    if (isPhoneVerified) {
      setTimeout(redirectToNextAuthStep, delay);
    }
    return () => clearTimeout(redirectToNextAuthStep, delay);
  }, [isPhoneVerified, redirectToNextAuthStep]);

  const isValidData = () => {
    return contactInfo.localNumber.length > 0 && validateFieldPhone(fullPhoneNumber);
  };

  const checkData = () => {
    if (!validateFieldPhone(fullPhoneNumber)) {
      setErrors(prev => ({...prev, phone: 'invalid_phone_number_error'}));
    } else {
      setErrors(prev => ({ ...prev, phone: null }));
    }
  };

  const handleCountryChange = (option) => {
    if (option.isDisabled) return;

    setContactInfo(prev => ({
      ...prev,
      countryCode: option.value
    }));

    setErrors(prev => ({ ...prev, phone: null }));
  };

  const handleLocalNumberChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '');

    if (digitsOnly.length <= 12) {
      setContactInfo(prev => ({
        ...prev,
        localNumber: digitsOnly
      }));

      if (digitsOnly.length > 0 && digitsOnly[0] === '0') {
        setErrors(prev => ({...prev, phone: 'phone_number_cannot_start_with_0'}));
      } else {
        setErrors(prev => ({...prev, phone: null}));
      }
    }
  };

  const updateContactInfo = (name, value) => {
    if (name === 'isPersonalPhone') {
      setContactInfo(prev => ({
        ...prev,
        isPersonalPhone: value
      }));
    }
  };


  const handleNext = async () => {
    const userFromLocaleStorage = getUserFromLocalstorage();
    const body = {
      ...userFromLocaleStorage,
      phone: fullPhoneNumber,
      isPersonal: contactInfo.isPersonalPhone,
      countryCode: contactInfo.countryCode
    };

    if (isDebugMode) {
      setDebugPayload(body);
      return;
    }

    setLoading(true);
    try {
      const data = await signup(body);
      setIsPhoneVerified(true);
      setLoading(false);
      logAnalytics('Self-registration signup success');
      localStorage.setItem('user', JSON.stringify({...data}));
    } catch (err) {
      logAnalytics('Self-registration signup error', {
        error: err.message
      });
      'Contact info not unique';
      if (err.message.includes('Contact info not unique type: Interpreter')) {
        setErrors(prev => ({...prev, ['notUniquePhone']: 'already_registered_phone'}));
        const stateName = getViewState(err.message);
        if (stateName) {
          logAnalytics(`Self-registration ${stateName} substep`, {
            name: `${user.fistName} ${user.lastName}`,
            email: user.email,
            phone: user.phone
          });
        }
      } else {
        showError(i18n('error500'));
      }
      setLoading(false);
    }
  };

  const setPhoneRelation = (checked, value) => {
    if (checked) {
      if (value === 'personal') {
        updateContactInfo('isPersonalPhone', true);
      }
      if (value === 'corporate') {
        updateContactInfo('isPersonalPhone', false);
      }
    }
  };

  return (
    <ContainerStepBox>
      {loading && (<Spinner overlay={true} />)}
      <PhoneVerificationWrapper>
        <ContentStepBox>
        {isDebugMode && (
          <DebugBanner>
            🔧 DEBUG MODE: Form will show payload instead of submitting
          </DebugBanner>
        )}
        <FlexRow>
            <StepsHeader>{i18n('contact_information')}</StepsHeader>
        </FlexRow>
        <FlexRow>
          <FlexItem>
            <StepLable data-test='contactInfoStepCountryLabel'>
              {i18n('app_country')}
              <span> *</span>
            </StepLable>
            <Select
              value={countryOptions.find(opt => opt.value === contactInfo.countryCode)}
              options={countryOptions}
              onChange={handleCountryChange}
              isSearchable={true}
              isOptionDisabled={(option) => option.isDisabled}
              placeholder={i18n('country_placeholder')}
              data-test='contactInfoStepCountrySelect'
              styles={countrySelectStyles}
            />
          </FlexItem>
        </FlexRow>
        <FlexRow>
          <FlexItem>
            <StepLable
              data-test='contactInfoStepPhoneLabel'
            >
              {i18n('contact_info_phone')}
              <span> *</span>
            </StepLable>
            <PhoneInputWrapper
              $focused={phoneInputFocused}
              $error={!!errors.phone}
              $color={colors.tulkaMainColor}
            >
              <CountryCodePrefix>
                {countryPhoneCodes[contactInfo.countryCode] || '+358'}
              </CountryCodePrefix>
              <PhoneInputStyled
                type="tel"
                value={contactInfo.localNumber}
                onChange={(e) => handleLocalNumberChange(e.target.value)}
                onFocus={() => setPhoneInputFocused(true)}
                onBlur={() => {
                  setPhoneInputFocused(false);
                  checkData();
                }}
                placeholder="401234567"
                data-test='contactInfoStepPhoneInput'
              />
            </PhoneInputWrapper>
            {errors.phone ? (
              <StyledErrorMessage
                color={colors.error}
              >
                {i18n(errors.phone)}
              </StyledErrorMessage>
            ) : null}
          </FlexItem>
        </FlexRow>
          <FlexRow>
            <FlexItem>
              <RelationBlock 
                type='phone'
                isPersonal={contactInfo.isPersonalPhone} 
                setRelation={setPhoneRelation}
              />
            </FlexItem>
          </FlexRow>
          <FlexRow>
            {errors.notUniquePhone && (
              <StyledErrorMessage color={colors.error}>
                {i18n(errors.notUniquePhone)}
                <a href={customerServicePhoneLink}>{customerServicePhone}</a>
              </StyledErrorMessage>
            )}
          </FlexRow>
          {debugPayload && (
            <FlexRow>
              <FlexItem>
                <h3 style={{ marginBottom: '8px' }}>POST Payload to /auth/signup:</h3>
                <DebugPayloadBox>
                  {JSON.stringify(debugPayload, null, 2)}
                </DebugPayloadBox>
              </FlexItem>
            </FlexRow>
          )}
      </ContentStepBox>
      </PhoneVerificationWrapper>
      <RoundButton
        data-test='selfRegistrationNextBtn'
        onClick={handleNext}
        padding='25'
        darkButton={true}
        disabled={!isValidData()}
      >
        <CollapsedTextButton>
          <span>{i18n('continue')}</span>
          <Icon
            icon='chevron_right'
            iconLib='material'
            iconSize={30}
            withoutContainer
          />
        </CollapsedTextButton>
      </RoundButton>
      <InfoBlock>
        {i18n('already_have_an_account_part1')}
        <a href='/login'>{i18n('already_have_an_account_part2')}</a>
      </InfoBlock>
    </ContainerStepBox>
  );
};

export const VerificationPage = () => {
  const search = useLocation().search;
  const token = new URLSearchParams(search).get('token');
  const [code] = useState(getData('code'));
  const history = useHistory();
  const [isVerificationFailed, setIsVerificationFailed] = useState(false);
  const [messageKey, setMessageKey] = useState('waiting_for_verification');
  const [ignored, forceUpdate] = useReducer(x => x + 1, 0); // eslint-disable-line no-unused-vars

  useEffect(() => {
    const func = async () => {
      forceUpdate();
    };
    document.addEventListener('changeLang', func);
    return () => {
      document.removeEventListener('changeLang', func);
    };
  }, []);

  const redirectToNextAuthStep = useCallback(async (data) => {
    const user = getUserFromLocalstorage();
    let url = '/#!';
    if (data?.verificationStatus === 'pending') {
      url = url.concat(getSelfRegistrationRoutes().phone);
    } else if (user?.token) {
      url = url.concat(getSelfRegistrationRoutes().cost_center);
    } else {
      url = url.concat(getSelfRegistrationRoutes().email);
    }
    history.replace(url);
  }, [history]);

  const getVerification = useCallback(async (codeOrToken) => {
    try {
      const data = await verify(codeOrToken);
      localStorage.setItem('user', JSON.stringify({...data}));
      await redirectToNextAuthStep(data);
    } catch (err) {
      if (err.message.includes('Invalid verification token')) {
        setMessageKey('verification_link_expired');
        setIsVerificationFailed(true);
      } else if (err.message.includes('User group not found')) {
        setMessageKey('user_group_not_found');
        setIsVerificationFailed(true);
      } else {
        setMessageKey('error500');
      }
    }
  }, [redirectToNextAuthStep]);

  useEffect(() => {
    if (token) {
      getVerification(token);
    } else if (code) {
      getVerification(code);
      localStorage.removeItem('code');
    }

  }, [token, code, getVerification]);

  const handleBack = () => {
    history.replace(getSelfRegistrationRoutes().email);
  };

  return (
    <ContainerStepBox>
      <ContentStepBox>
        <FlexRow>
            <StepsHeader>{i18n('verification_page')}</StepsHeader>
        </FlexRow>
        <FlexRow>
          <FlexItem>{i18n(messageKey)}</FlexItem>
        </FlexRow>
      </ContentStepBox>
      {isVerificationFailed && (
        <>
          <RoundButton
            data-test='selfRegistrationNextBtn'
            onClick={handleBack}
            padding='25'
            darkButton={true}
          >
            <CollapsedTextButton>
              <span>{i18n('back')}</span>
              <Icon
                icon='chevron_right'
                iconLib='material'
                iconSize={30}
                withoutContainer
              />
            </CollapsedTextButton>
          </RoundButton>
          <InfoBlock>
            {i18n('already_have_an_account_part1')}
            <a href='/login'>{i18n('already_have_an_account_part2')}</a>
          </InfoBlock>
        </>
      )}
    </ContainerStepBox>
  );
};
